import { useMemo, type ReactNode } from "react";
import { usePermissions } from "react-admin";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useTheme } from "@mui/material/styles";
import { useItsOneViewForIts } from "@/hooks/useItsOneView";
import { formatInr } from "@/utils/format-inr";
import { buildRazaPrintCommitmentSummaries } from "@/utils/its-one-view-commitment-summaries";
import { hasPermission } from "@/utils/permission-utils";

type RazaPrintCommitmentsBlockProps = {
  itsNo?: string | null;
  /** Matches schedule table density for A4/A5 print. */
  cellFontSizePx?: number;
  mt?: number;
};

function TableCellComp({
  children,
  align,
  fontSizePx,
}: {
  children: ReactNode;
  align?: "left" | "right" | "center";
  fontSizePx: number;
}) {
  const theme = useTheme();
  return (
    <TableCell
      align={align}
      sx={{
        fontFamily: theme.typography.fontFamily,
        fontSize: fontSizePx,
        textTransform: align === "right" ? "none" : "capitalize",
      }}
    >
      {children}
    </TableCell>
  );
}

/** Compact commitment summary for the raza hardcopy (print) form. */
export function RazaPrintCommitmentsBlock({
  itsNo,
  cellFontSizePx = 16,
  mt = 3,
}: RazaPrintCommitmentsBlockProps) {
  const { permissions } = usePermissions();
  const canView = hasPermission(permissions, "itsOneView.view");

  const { data, loading, error, itsValid } = useItsOneViewForIts(itsNo, {
    persistLastIts: false,
    includeSettled: true,
    enabled: canView,
  });

  const modules = useMemo(() => (data ? buildRazaPrintCommitmentSummaries(data) : []), [data]);

  if (!canView || !itsValid) return null;

  if (loading && !data) {
    return (
      <Box mt={mt} display="flex" justifyContent="center">
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error || modules.length === 0) return null;

  return (
    <Box mt={mt}>
      <Paper variant="outlined">
        <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCellComp fontSizePx={cellFontSizePx}>Particulars</TableCellComp>
              <TableCellComp fontSizePx={cellFontSizePx} align="right">
                Due
              </TableCellComp>
              <TableCellComp fontSizePx={cellFontSizePx} align="right">
                Paid
              </TableCellComp>
              <TableCellComp fontSizePx={cellFontSizePx} align="right">
                Total
              </TableCellComp>
            </TableRow>
          </TableHead>
          <TableBody>
            {modules.map((row) => (
              <TableRow key={row.key}>
                <TableCellComp fontSizePx={cellFontSizePx}>{row.label}</TableCellComp>
                <TableCellComp fontSizePx={cellFontSizePx} align="right">
                  {formatInr(row.balance)}
                </TableCellComp>
                <TableCellComp fontSizePx={cellFontSizePx} align="right">
                  {formatInr(row.paid)}
                </TableCellComp>
                <TableCellComp fontSizePx={cellFontSizePx} align="right">
                  {formatInr(row.total)}
                </TableCellComp>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
